<script setup lang="ts">
import { HgFormFieldStateIcons, HgFormFieldWrapper, HgIcon } from '@smg-real-estate/domus';
import { reactive, ref } from 'vue';
import { VueTelInput } from 'vue-tel-input';

const MyPhoneInput = VueTelInput as any;

interface Props {
  /**
   * If the component is disabled or not.
   */
  disabled?: boolean;
  /**
   * Label to display above the text area.
   */
  label?: string;
  /**
   * Name of the text area element.
   */
  name: string;
  /**
   * Incoming data from v-model will default to a prop named modelValue.
   */
  modelValue?: string;
  /**
   * Set to true if field is required. Does not produce any visual output. Does not influence validation.
   */
  isRequired?: boolean;
  /**
   * Set to true if field is required. Does not influence validation but display required icon.
   */
  required?: boolean;
  /**
   * Set to true if the field is invalid. Enables the display of error styling and error messages.
   */
  isInvalid?: boolean;
  /**
   * Set to true if the label is already defined within component
   */
  hideLabel?: boolean;
  /**
   * Set to true if the field is dirty.
   * Setting this to true enables the display of the valid/error style and error messages.
   */
  isDirty?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  label: undefined,
  modelValue: '',
  isRequired: false,
  required: false,
  isInvalid: false,
  hideLabel: true,
  isDirty: false,
});

const emit = defineEmits<{
  'update:modelValue': [value: any];
  blur: [];
}>();

const UPDATE_MODEL_VALUE = 'update:modelValue';

const internalModel = ref(props.modelValue);

const bindProps = reactive({
  mode: 'national',
  defaultCountry: 'CH',
  inputOptions: {
    required: props.isRequired,
    placeholder: '',
    autocomplete: 'on',
    name: props.name,
    maxlength: 16, // From ITU-T Recommendation, E. 164 numbers can have a maximum of 15 digits.
    type: 'tel',
  },
  dropdownOptions: {
    showDialCodeInSelection: true,
    showDialCodeInList: true,
    showFlags: false,
  },
  validCharactersOnly: true,
  preferredCountries: ['CH', 'DE', 'AT', 'FR', 'IT'],
});

// The function is triggered on mounted, and whenever model value change
// edge case: in some cases, ex: when number.length is 1
// the obj will have no "number" attribute, and "valid" value is undefined
const onInputHandler = (number: string, obj: any): void => {
  if (!number) {
    return emit(UPDATE_MODEL_VALUE, { valid: true, number: null });
  }

  return emit(UPDATE_MODEL_VALUE, obj);
};

const handleBlur = (): void => {
  emit('blur');
};
</script>

<!-- Wrapper component for VueTelInput https://www.npmjs.com/package/vue-tel-input -->
<template>
  <div>
    <label>
      <div :class="$style.label">
        {{ label }}
        <span v-if="required || isRequired" :class="$style.requiredMark">*</span>
      </div>
    </label>
    <HgFormFieldWrapper v-bind="$props" label="" :class="$style.formFieldWrapper">
      <HgFormFieldStateIcons v-bind="$props" :iconClass="$style.icon">
        <MyPhoneInput
          v-model.lazy="internalModel"
          v-bind="bindProps"
          @onInput="onInputHandler"
          @blur="handleBlur"
        >
          <template #arrow-icon>
            <HgIcon icon="ChevronDown" :themeClass="$style.iconTheme" />
          </template>
        </MyPhoneInput>
      </HgFormFieldStateIcons>
      <!-- @slot Any slot supported by the HgFormFieldWrapper component. -->
      <template v-for="(_, slotName) in $slots" #[slotName]>
        <slot :name="slotName" />
      </template>
    </HgFormFieldWrapper>
  </div>
</template>

<style lang="scss" module>
@use '@/assets/variables' as *;

.label {
  cursor: auto;

  @include label;
}

.icon {
  top: 50%;
}

.iconTheme {
  path {
    fill: $color-grey-80;
  }
}

.formFieldWrapper {
  padding-top: 0;

  div[class*='tooltip'] {
    /* Compensate the height introduced by <label> */
    top: -22px;
  }
}

.requiredMark {
  color: $color-blue-60;
}
</style>
